import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";

import { api } from "../api/client.ts";
import { ImageUpload } from "./image-upload.tsx";
import { Button, ErrorBlock, Input } from "./ui.tsx";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";

/**
 * ═══════════════════════════════════════════════════════════════
 *  供应商上架商品
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **以前供应商后台只能改库存和发货 —— 建商品、加规格、传图一个都做不了。**
 *    商品全靠种子脚本预置，真实供应商入驻之后打开后台是一片空白，
 *    连第一件货都上不了。
 *
 * 🔴 **供货价和指导零售价是两个不同的东西，界面上必须讲清楚：**
 *    - **供货价** = 平台从你这儿拿货的价，**对下级永远不可见**
 *    - **指导零售价（吊牌价）** = 不认识人的消费者看到的价，是全链的天花板
 *    分不清的话供应商会把吊牌价填成供货价，全链立刻没有利润空间。
 */

interface SpecRow {
  key: string;
  value: string;
}

interface SkuDraft {
  specs: SpecRow[];
  supplyPrice: string;
  listPrice: string;
  stock: string;
  unit: string;
  weightGram: string;
}

const emptySku = (): SkuDraft => ({
  specs: [{ key: "规格", value: "" }],
  supplyPrice: "",
  listPrice: "",
  stock: "0",
  unit: "件",
  weightGram: "",
});

/** 元 → 分。⚠️ 用户填的是元，库里全程是分。 */
function yuanToFen(s: string): number {
  const n = Number(s);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.round(n * 100);
}

export function ProductEditor({
  orgId,
  onDone,
  onCancel,
}: {
  orgId: number;
  onDone: () => void;
  onCancel: () => void;
}) {
  const qc = useQueryClient();
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [categoryId, setCategoryId] = useState<number | "">("");
  const [imageKeys, setImageKeys] = useState<string[]>([]);
  const [detailImageKeys, setDetailImageKeys] = useState<string[]>([]);
  const [descMd, setDescMd] = useState("");
  const [installNote, setInstallNote] = useState("");
  const [skus, setSkus] = useState<SkuDraft[]>([emptySku()]);

  const categories = useQuery({
    queryKey: ["categories"],
    queryFn: () => api.get<{ id: number; name: string }[]>("/categories"),
  });

  const create = useMutation({
    mutationFn: async () => {
      /**
       * ⚠️ **商品和规格是两次请求**（后端就是两个接口）。
       *    商品建出来之后规格失败的话，会留下一个没有规格的草稿 ——
       *    那是可编辑状态，供应商能自己补，比整体回滚更好：
       *    否则他填的一长串信息全没了。
       */
      const product = await api.post<{ id: number }>("/merchant/products", {
        categoryId: Number(categoryId),
        title: title.trim(),
        ...(subtitle.trim() ? { subtitle: subtitle.trim() } : {}),
        ...(descMd.trim() ? { descMd: descMd.trim() } : {}),
        ...(installNote.trim() ? { installNote: installNote.trim() } : {}),
        imageKeys,
        detailImageKeys,
      });

      for (const s of skus) {
        const specs: Record<string, string> = {};
        for (const r of s.specs) {
          if (r.key.trim() && r.value.trim()) specs[r.key.trim()] = r.value.trim();
        }
        await api.post("/merchant/skus", {
          productId: product.id,
          specs,
          supplyPrice: yuanToFen(s.supplyPrice),
          ...(yuanToFen(s.listPrice) > 0 ? { listPrice: yuanToFen(s.listPrice) } : {}),
          stock: Number(s.stock) || 0,
          unit: s.unit.trim() || "件",
          ...(Number(s.weightGram) > 0 ? { weightGram: Number(s.weightGram) } : {}),
        });
      }
      return product;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["supplier"] });
      onDone();
    },
  });

  const canSubmit =
    title.trim().length >= 2 &&
    categoryId !== "" &&
    skus.every((s) => yuanToFen(s.supplyPrice) > 0);

  return (
    <Card className="p-5">
      <h2 className="text-[15px] font-medium">新建商品</h2>
      <p className="mt-1 text-[12px] text-muted-foreground">
        建商品 → 加规格 → 提交审核 → 平台通过后自动上架
      </p>

      <div className="mt-5 grid gap-4 md:grid-cols-2">
        <div className="md:col-span-2">
          <Input
            label="商品名 *"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="如：东鹏 通体大理石瓷砖 800×800 雅白"
            maxLength={120}
          />
        </div>
        <Input
          label="副标题"
          value={subtitle}
          onChange={(e) => setSubtitle(e.target.value)}
          placeholder="一句话卖点"
          maxLength={120}
        />
        <div>
          <Label className="mb-1.5 block">品类 *</Label>
          <select
            className="h-11 w-full rounded-lg border border-input bg-background px-3 text-base"
            value={String(categoryId)}
            onChange={(e) => setCategoryId(e.target.value ? Number(e.target.value) : "")}
          >
            <option value="">请选择</option>
            {categories.data?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div className="md:col-span-2">
          <Label className="mb-1.5 block">商品图</Label>
          <ImageUpload
            value={imageKeys}
            onChange={setImageKeys}
            scope="product"
            orgId={orgId}
            hint="第一张是主图，会出现在列表和分享卡片上。建材看的是材质，实拍图比效果图管用。"
          />
        </div>

        <div className="md:col-span-2">
          <Label className="mb-1.5 block">商品详情</Label>
          <textarea
            className="min-h-24 w-full rounded-lg border border-input bg-background px-3 py-2 text-base"
            value={descMd}
            onChange={(e) => setDescMd(e.target.value)}
            placeholder="材质、工艺、适用空间…"
            maxLength={20000}
          />
        </div>
        <div className="md:col-span-2">
          <Label className="mb-1.5 block">施工提示</Label>
          <textarea
            className="min-h-16 w-full rounded-lg border border-input bg-background px-3 py-2 text-base"
            value={installNote}
            onChange={(e) => setInstallNote(e.target.value)}
            placeholder="如：铺贴前请核对批号与色号"
            maxLength={5000}
          />
        </div>
      </div>

      <div className="mt-6">
        <div className="mb-2 flex items-center justify-between">
          <Label>规格 *</Label>
          <button
            type="button"
            className="flex items-center gap-1 text-[13px] text-primary"
            onClick={() => setSkus((prev) => [...prev, emptySku()])}
          >
            <Plus className="size-3.5" />
            加一个规格
          </button>
        </div>

        <div className="space-y-3">
          {skus.map((s, i) => (
            <div key={i} className="rounded-lg border border-border p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-[13px] text-muted-foreground">规格 {i + 1}</span>
                {skus.length > 1 && (
                  <button
                    type="button"
                    className="text-muted-foreground active:opacity-60"
                    onClick={() => setSkus((prev) => prev.filter((_, j) => j !== i))}
                    aria-label="删除这个规格"
                  >
                    <Trash2 className="size-4" />
                  </button>
                )}
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                {s.specs.map((row, ri) => (
                  <div key={ri} className="flex gap-2">
                    <Input
                      className="w-28"
                      value={row.key}
                      placeholder="规格名"
                      onChange={(e) =>
                        setSkus((prev) =>
                          prev.map((x, j) =>
                            j === i
                              ? {
                                  ...x,
                                  specs: x.specs.map((r, k) =>
                                    k === ri ? { ...r, key: e.target.value } : r,
                                  ),
                                }
                              : x,
                          ),
                        )
                      }
                    />
                    <Input
                      value={row.value}
                      placeholder="如 800×800 雅白"
                      onChange={(e) =>
                        setSkus((prev) =>
                          prev.map((x, j) =>
                            j === i
                              ? {
                                  ...x,
                                  specs: x.specs.map((r, k) =>
                                    k === ri ? { ...r, value: e.target.value } : r,
                                  ),
                                }
                              : x,
                          ),
                        )
                      }
                    />
                  </div>
                ))}

                <Input
                  label="供货价（元）*"
                  inputMode="decimal"
                  value={s.supplyPrice}
                  placeholder="88.00"
                  onChange={(e) =>
                    setSkus((prev) =>
                      prev.map((x, j) => (j === i ? { ...x, supplyPrice: e.target.value } : x)),
                    )
                  }
                />
                <Input
                  label="指导零售价（元）"
                  inputMode="decimal"
                  value={s.listPrice}
                  placeholder="建议填供货价的 2~3 倍"
                  onChange={(e) =>
                    setSkus((prev) =>
                      prev.map((x, j) => (j === i ? { ...x, listPrice: e.target.value } : x)),
                    )
                  }
                />
                <Input
                  label="库存"
                  inputMode="numeric"
                  value={s.stock}
                  onChange={(e) =>
                    setSkus((prev) =>
                      prev.map((x, j) => (j === i ? { ...x, stock: e.target.value } : x)),
                    )
                  }
                />
                <Input
                  label="单位"
                  value={s.unit}
                  placeholder="片 / 件 / ㎡"
                  onChange={(e) =>
                    setSkus((prev) =>
                      prev.map((x, j) => (j === i ? { ...x, unit: e.target.value } : x)),
                    )
                  }
                />
                <Input
                  label="单件重量（克）"
                  inputMode="numeric"
                  value={s.weightGram}
                  placeholder="用来算运费"
                  onChange={(e) =>
                    setSkus((prev) =>
                      prev.map((x, j) => (j === i ? { ...x, weightGram: e.target.value } : x)),
                    )
                  }
                />
              </div>
            </div>
          ))}
        </div>

        {/*
          🔴 这两个价最容易被填反，所以把区别写在紧挨着输入框的地方，
             而不是藏在帮助文档里。
        */}
        <div className="mt-3 rounded-lg bg-secondary px-3 py-2.5 text-[12px] leading-relaxed text-muted-foreground">
          <p>
            <strong className="text-foreground">供货价</strong> ——
            平台从你这儿拿货的价。对下级商家<strong className="text-foreground">永远不可见</strong>。
          </p>
          <p className="mt-1">
            <strong className="text-foreground">指导零售价</strong> ——
            不认识人的消费者看到的价，也是<strong className="text-foreground">全链的价格天花板</strong>。
            各级商家在自己的进货价和它之间定价，谁让利多谁抢到客户。留空的话按链条加价算，
            消费者会看到偏低的价，中间商没有让利空间。
          </p>
        </div>
      </div>

      {/*
        ── 商品详情图 ──

        🔴 **跟商品图是两回事**（淘宝那套：商品图 vs 商品详情）：
           商品图是**商品长什么样** —— 列表卡片、详情页顶部轮播、购物车和订单
           缩略图全都取它，所以必须是方图；
           详情图是**介绍商品** —— 材质说明、施工示意、尺寸表，
           通常是几张竖着排的长图，只出现在详情页最下面。
           合成一组的话，一张 800×3000 的说明图会缩成 80×80 的方块跑进列表和订单。

        ⚠️ **摆在规格后面**、并且**整张铺开**：这一格就是详情页最下面那一段的预览，
           传完立刻看到客户会看到什么 —— 缩略图看不出长图上写了什么，也看不出顺序。
      */}
      <div className="mt-6">
        <Label className="mb-1.5 block">商品详情图（选填）</Label>
        <p className="mb-2 text-[12px] text-muted-foreground">
          下面就是客户在详情页最下面看到的样子，从上往下排。
        </p>
        <ImageUpload
          value={detailImageKeys}
          onChange={setDetailImageKeys}
          scope="product"
          orgId={orgId}
          max={20}
          label="添加详情图"
          variant="strip"
        />
      </div>

      {create.error && <ErrorBlock error={create.error} />}

      <div className="mt-5 flex gap-2">
        <Button loading={create.isPending} disabled={!canSubmit} onClick={() => create.mutate()}>
          创建（存为草稿）
        </Button>
        <Button variant="outline" onClick={onCancel}>
          取消
        </Button>
      </div>
    </Card>
  );
}
